import React, { useState } from 'react';
import { UserPlus, CheckCircle, LogOut, User, Sparkles, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';
interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}
interface PatientFormData {
  name: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  emergencyContact: EmergencyContact;
}
export function PatientSelfRegisterPage() {
  const {
    logout,
    user
  } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredPatientName, setRegisteredPatientName] = useState('');
  const [formData, setFormData] = useState<PatientFormData>({
    name: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });
  const handleInputChange = (field: keyof PatientFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };
  const handleEmergencyContactChange = (field: keyof EmergencyContact, value: string) => {
    setFormData(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
    setError(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      // Validate required fields
      if (!formData.name || !formData.gender || !formData.phone) {
        setError('Por favor complete los campos obligatorios: Nombre, Género y Teléfono');
        setIsSubmitting(false);
        return;
      }
      // Direct Supabase insert bypassing DataContext permission checks
      const {
        data: insertedPatient,
        error: insertError
      } = await supabase.from('patients').insert([{
        name: formData.name,
        gender: formData.gender,
        phone: formData.phone,
        email: formData.email || null,
        address: formData.address || null,
        emergency_contact: formData.emergencyContact,
        created_at: new Date().toISOString()
      }]).select().single();
      if (insertError) {
        console.error('Supabase insert error:', insertError);
        setError(`Error al registrar: ${insertError.message}`);
        setIsSubmitting(false);
        return;
      }
      // Create audit log entry
      try {
        await supabase.from('audit_logs').insert([{
          user_id: user?.id || 'paciente-system',
          user_name: user?.name || 'Sistema de Registro de Pacientes',
          action: 'create',
          module: 'patients',
          entity_id: insertedPatient.id,
          entity_name: formData.name,
          details: `Paciente registrado mediante auto-registro: ${formData.name} (${formData.phone})`,
          created_at: new Date().toISOString()
        }]);
      } catch (auditError) {
        console.error('Error creating audit log:', auditError);
        // Don't fail the registration if audit log fails
      }
      setRegisteredPatientName(formData.name);
      setIsSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al registrar paciente');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleRegisterAnother = () => {
    setIsSuccess(false);
    setRegisteredPatientName('');
    setFormData({
      name: '',
      gender: '',
      phone: '',
      email: '',
      address: '',
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      }
    });
    setError(null);
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4126">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-teal-100" data-id="element-4127">
          {/* Success Header with Animation */}
          <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 p-10 text-center overflow-hidden" data-id="element-4128">
            <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" data-id="element-4129"></div>
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-4130"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" data-id="element-4131"></div>

            <div className="relative z-10" data-id="element-4132">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-md rounded-full mb-6 animate-bounce" data-id="element-4133">
                <CheckCircle className="w-14 h-14 text-white" strokeWidth={2.5} data-id="element-4134" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-3" data-id="element-4135">
                ¡Registro Exitoso!
              </h1>
              <div className="flex items-center justify-center gap-2 text-teal-50" data-id="element-4136">
                <Sparkles className="w-4 h-4" data-id="element-4137" />
                <p className="text-sm font-medium" data-id="element-4138">Bienvenido a Savidna</p>
                <Sparkles className="w-4 h-4" data-id="element-4139" />
              </div>
            </div>
          </div>

          <div className="p-8" data-id="element-4140">
            {/* Success Message */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl p-6 mb-6" data-id="element-4141">
              <div className="flex items-start gap-4" data-id="element-4142">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center" data-id="element-4143">
                  <User className="w-6 h-6 text-teal-600" data-id="element-4144" />
                </div>
                <div className="flex-1" data-id="element-4145">
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-4146">
                    {registeredPatientName}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-4147">
                    Su información ha sido registrada correctamente en nuestro
                    sistema. Nuestro equipo se pondrá en contacto con usted
                    próximamente.
                  </p>
                </div>
              </div>
            </div>

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6 pb-6 border-b border-gray-100" data-id="element-4148">
              <Shield className="w-4 h-4 text-teal-600" data-id="element-4149" />
              <span data-id="element-4150">Sus datos están protegidos y seguros</span>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3" data-id="element-4151">
              <button onClick={handleRegisterAnother} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-3 shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:-translate-y-0.5" data-id="element-4152">
                <UserPlus className="w-5 h-5" data-id="element-4153" />
                Registrar Otro Paciente
              </button>

              <button onClick={logout} className="w-full bg-gray-50 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-200 flex items-center justify-center gap-3 border border-gray-200" data-id="element-4154">
                <LogOut className="w-5 h-5" data-id="element-4155" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4156">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden border border-teal-100" data-id="element-4157">
        {/* Modern Header */}
        <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 p-10 text-center overflow-hidden" data-id="element-4158">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" data-id="element-4159"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-4160"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" data-id="element-4161"></div>

          <div className="relative z-10" data-id="element-4162">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-md rounded-2xl mb-4" data-id="element-4163">
              <User className="w-10 h-10 text-white" strokeWidth={2} data-id="element-4164" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2" data-id="element-4165">
              Registro de Paciente
            </h1>
            <p className="text-teal-50 text-lg font-medium" data-id="element-4166">Sistema Savidna</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full text-sm text-teal-50" data-id="element-4167">
              <Shield className="w-4 h-4" data-id="element-4168" />
              <span data-id="element-4169">Información segura y confidencial</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 md:p-10" data-id="element-4170">
          {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg" data-id="element-4171">
              <div className="flex items-start gap-3" data-id="element-4172">
                <div className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5" data-id="element-4173">
                  <span className="text-red-600 text-xs font-bold" data-id="element-4174">!</span>
                </div>
                <p className="text-sm text-red-700 font-medium" data-id="element-4175">{error}</p>
              </div>
            </div>}

          <div className="space-y-8" data-id="element-4176">
            {/* Información Personal */}
            <div className="relative" data-id="element-4177">
              <div className="flex items-center gap-3 mb-5" data-id="element-4178">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl text-white font-bold shadow-lg shadow-teal-500/25" data-id="element-4179">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-4180">
                  Información Personal
                </h3>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-teal-50/30 rounded-2xl p-6 border border-gray-100" data-id="element-4181">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4182">
                  <div className="md:col-span-2" data-id="element-4183">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4184">
                      Nombre Completo <span className="text-red-500" data-id="element-4185">*</span>
                    </label>
                    <input type="text" value={formData.name} onChange={e => handleInputChange('name', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white" placeholder="Ingrese su nombre completo" required data-id="element-4186" />
                  </div>

                  <div data-id="element-4187">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4188">
                      Género <span className="text-red-500" data-id="element-4189">*</span>
                    </label>
                    <select value={formData.gender} onChange={e => handleInputChange('gender', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white" required data-id="element-4190">
                      <option value="" data-id="element-4191">Seleccionar</option>
                      <option value="masculino" data-id="element-4192">Masculino</option>
                      <option value="femenino" data-id="element-4193">Femenino</option>
                      <option value="otro" data-id="element-4194">Otro</option>
                    </select>
                  </div>

                  <div data-id="element-4195">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4196">
                      Teléfono <span className="text-red-500" data-id="element-4197">*</span>
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white" placeholder="809-000-0000" required data-id="element-4198" />
                  </div>
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div className="relative" data-id="element-4199">
              <div className="flex items-center gap-3 mb-5" data-id="element-4200">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl text-white font-bold shadow-lg shadow-cyan-500/25" data-id="element-4201">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-4202">
                  Información de Contacto
                </h3>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-cyan-50/30 rounded-2xl p-6 border border-gray-100" data-id="element-4203">
                <div className="grid grid-cols-1 gap-5" data-id="element-4204">
                  <div data-id="element-4205">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4206">
                      Email
                    </label>
                    <input type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white" placeholder="correo@ejemplo.com" data-id="element-4207" />
                  </div>

                  <div data-id="element-4208">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4209">
                      Dirección
                    </label>
                    <input type="text" value={formData.address} onChange={e => handleInputChange('address', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white" placeholder="Calle, número, sector, ciudad" data-id="element-4210" />
                  </div>
                </div>
              </div>
            </div>

            {/* Contacto de Emergencia */}
            <div className="relative" data-id="element-4211">
              <div className="flex items-center gap-3 mb-5" data-id="element-4212">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl text-white font-bold shadow-lg shadow-rose-500/25" data-id="element-4213">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-4214">
                  Contacto de Emergencia
                </h3>
              </div>
              <div className="bg-gradient-to-br from-rose-50/50 to-pink-50/30 rounded-2xl p-6 border border-rose-100" data-id="element-4215">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4216">
                  <div data-id="element-4217">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4218">
                      Nombre
                    </label>
                    <input type="text" value={formData.emergencyContact.name} onChange={e => handleEmergencyContactChange('name', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all bg-white" placeholder="Nombre del contacto" data-id="element-4219" />
                  </div>

                  <div data-id="element-4220">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4221">
                      Relación
                    </label>
                    <input type="text" value={formData.emergencyContact.relationship} onChange={e => handleEmergencyContactChange('relationship', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all bg-white" placeholder="Ej: Madre, Esposo/a, Hijo/a" data-id="element-4222" />
                  </div>

                  <div className="md:col-span-2" data-id="element-4223">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4224">
                      Teléfono
                    </label>
                    <input type="tel" value={formData.emergencyContact.phone} onChange={e => handleEmergencyContactChange('phone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all bg-white" placeholder="Número de contacto de emergencia" data-id="element-4225" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-4226">
            <button type="submit" disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-xl shadow-teal-500/25 hover:shadow-2xl hover:shadow-teal-500/30 hover:-translate-y-0.5" data-id="element-4227">
              {isSubmitting ? <>
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" data-id="element-4228" />
                  Registrando...
                </> : <>
                  <UserPlus className="w-6 h-6" data-id="element-4229" />
                  Registrar Paciente
                </>}
            </button>

            <button type="button" onClick={logout} className="sm:w-auto bg-gray-50 text-gray-700 py-4 px-8 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-200 flex items-center justify-center gap-3 border-2 border-gray-200" data-id="element-4230">
              <LogOut className="w-5 h-5" data-id="element-4231" />
              Salir
            </button>
          </div>
        </form>
      </div>
    </div>;
}